import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

const STABLE_ID_PATTERN = "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$";
const IDEMPOTENCY_KEY_PATTERN = "^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$";
const ANNOUNCEMENT_NUMBER_PATTERN = /^GG-(\d{4})(\d{2})(\d{2})-([0-9A-F]{12})$/u;
const MAX_ATTACHMENT_BYTES = 2 * 1024 * 1024;
const MAX_TOTAL_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const FNV_1A_64_OFFSET = 0xcbf29ce484222325n;
const FNV_1A_64_PRIME = 0x100000001b3n;
const LOWER_48_BITS = 0xffffffffffffn;

const StableIdSchema = Type.String({ minLength: 1, maxLength: 128, pattern: STABLE_ID_PATTERN });
const IdempotencyKeySchema = Type.String({ pattern: IDEMPOTENCY_KEY_PATTERN });
const DateTimeSchema = Type.String({ minLength: 24, maxLength: 24 });
const NullableDateTimeSchema = Type.Union([DateTimeSchema, Type.Null()]);
const VersionSchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });

export const AnnouncementAudienceRoleSchema = Type.Union([
  Type.Literal("OWNER"),
  Type.Literal("MANAGER"),
  Type.Literal("CASHIER"),
  Type.Literal("WAITER"),
  Type.Literal("COACH"),
]);
export type AnnouncementAudienceRole = Static<typeof AnnouncementAudienceRoleSchema>;

export const AnnouncementLifecycleSchema = Type.Union([
  Type.Literal("DRAFT"),
  Type.Literal("SCHEDULED"),
  Type.Literal("ACTIVE"),
  Type.Literal("EXPIRED"),
  Type.Literal("WITHDRAWN"),
]);
export type AnnouncementLifecycle = Static<typeof AnnouncementLifecycleSchema>;

export const AnnouncementDeliveryStateSchema = Type.Union([
  Type.Literal("PENDING"),
  Type.Literal("DELIVERED"),
  Type.Literal("READ"),
  Type.Literal("ACKNOWLEDGED"),
]);
export type AnnouncementDeliveryState = Static<typeof AnnouncementDeliveryStateSchema>;

export const AnnouncementNumberSchema = Type.String({
  pattern: "^GG-[0-9]{8}-[0-9A-F]{12}$",
  minLength: 24,
  maxLength: 24,
});
export type AnnouncementNumber = Static<typeof AnnouncementNumberSchema>;

const AttachmentContentTypeSchema = Type.Union([
  Type.Literal("application/pdf"),
  Type.Literal("image/jpeg"),
  Type.Literal("image/png"),
  Type.Literal("application/vnd.openxmlformats-officedocument.wordprocessingml.document"),
  Type.Literal("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"),
]);

export const AnnouncementAttachmentInputSchema = Type.Object(
  {
    fileName: Type.String({ minLength: 1, maxLength: 120 }),
    contentType: AttachmentContentTypeSchema,
    sizeBytes: Type.Integer({ minimum: 1, maximum: MAX_ATTACHMENT_BYTES }),
    sha256: Type.String({ pattern: "^[0-9a-f]{64}$" }),
    contentBase64: Type.String({ minLength: 4, maxLength: 2_796_204 }),
  },
  { additionalProperties: false },
);
export type AnnouncementAttachmentInput = Static<typeof AnnouncementAttachmentInputSchema>;

export const AnnouncementAttachmentReadModelSchema = Type.Object(
  {
    attachmentId: StableIdSchema,
    fileName: Type.String({ minLength: 1, maxLength: 120 }),
    contentType: AttachmentContentTypeSchema,
    sizeBytes: Type.Integer({ minimum: 1, maximum: MAX_ATTACHMENT_BYTES }),
    sha256: Type.String({ pattern: "^[0-9a-f]{64}$" }),
    downloadPath: Type.String({ minLength: 1, maxLength: 512 }),
  },
  { additionalProperties: false },
);
export type AnnouncementAttachmentReadModel = Static<typeof AnnouncementAttachmentReadModelSchema>;

const ContentProperties = {
  title: Type.String({ minLength: 2, maxLength: 80 }),
  body: Type.String({ minLength: 2, maxLength: 4000 }),
  pinned: Type.Boolean(),
  startsAt: DateTimeSchema,
  expiresAt: DateTimeSchema,
  audienceRoles: Type.Array(AnnouncementAudienceRoleSchema, { minItems: 1, maxItems: 5 }),
  attachments: Type.Array(AnnouncementAttachmentInputSchema, { maxItems: 3 }),
};

export const CreateAnnouncementDraftRequestSchema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    ...ContentProperties,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CreateAnnouncementDraftRequest = Static<typeof CreateAnnouncementDraftRequestSchema>;

export const UpdateAnnouncementDraftRequestSchema = Type.Object(
  { ...ContentProperties, expectedVersion: VersionSchema, idempotencyKey: IdempotencyKeySchema },
  { additionalProperties: false },
);
export type UpdateAnnouncementDraftRequest = Static<typeof UpdateAnnouncementDraftRequestSchema>;

export const PublishAnnouncementRequestSchema = Type.Object(
  { expectedVersion: VersionSchema, idempotencyKey: IdempotencyKeySchema },
  { additionalProperties: false },
);
export type PublishAnnouncementRequest = Static<typeof PublishAnnouncementRequestSchema>;

export const WithdrawAnnouncementRequestSchema = Type.Object(
  {
    expectedVersion: VersionSchema,
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type WithdrawAnnouncementRequest = Static<typeof WithdrawAnnouncementRequestSchema>;

export const RetryAnnouncementDeliveryRequestSchema = Type.Object(
  { expectedVersion: VersionSchema, idempotencyKey: IdempotencyKeySchema },
  { additionalProperties: false },
);
export type RetryAnnouncementDeliveryRequest = Static<
  typeof RetryAnnouncementDeliveryRequestSchema
>;

export const MarkAnnouncementDeliveryRequestSchema = Type.Object(
  {
    action: Type.Union([
      Type.Literal("DELIVER"),
      Type.Literal("READ"),
      Type.Literal("ACKNOWLEDGE"),
    ]),
    expectedAnnouncementVersion: VersionSchema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type MarkAnnouncementDeliveryRequest = Static<typeof MarkAnnouncementDeliveryRequestSchema>;

export const ReplyAnnouncementRequestSchema = Type.Object(
  {
    expectedAnnouncementVersion: VersionSchema,
    body: Type.String({ minLength: 1, maxLength: 500 }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type ReplyAnnouncementRequest = Static<typeof ReplyAnnouncementRequestSchema>;

export const AnnouncementReplyReadModelSchema = Type.Object(
  {
    replyId: StableIdSchema,
    authorDisplayName: Type.String({ minLength: 1, maxLength: 120 }),
    authorRole: AnnouncementAudienceRoleSchema,
    body: Type.String({ minLength: 1, maxLength: 500 }),
    repliedAt: DateTimeSchema,
  },
  { additionalProperties: false },
);
export type AnnouncementReplyReadModel = Static<typeof AnnouncementReplyReadModelSchema>;

export const AnnouncementRecipientSummarySchema = Type.Object(
  {
    accountId: StableIdSchema,
    displayName: Type.String({ minLength: 1, maxLength: 120 }),
    role: AnnouncementAudienceRoleSchema,
    state: AnnouncementDeliveryStateSchema,
    deliveryAttemptCount: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    deliveredAt: NullableDateTimeSchema,
    readAt: NullableDateTimeSchema,
    acknowledgedAt: NullableDateTimeSchema,
    replyCount: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
  },
  { additionalProperties: false },
);
export type AnnouncementRecipientSummary = Static<typeof AnnouncementRecipientSummarySchema>;

export const AnnouncementReadModelSchema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    announcementId: StableIdSchema,
    announcementNumber: AnnouncementNumberSchema,
    tenantId: StableIdSchema,
    storeId: StableIdSchema,
    version: VersionSchema,
    eventRevision: VersionSchema,
    lifecycle: AnnouncementLifecycleSchema,
    title: Type.String({ minLength: 2, maxLength: 80 }),
    body: Type.String({ minLength: 2, maxLength: 4000 }),
    pinned: Type.Boolean(),
    startsAt: DateTimeSchema,
    expiresAt: DateTimeSchema,
    audienceRoles: Type.Array(AnnouncementAudienceRoleSchema, { minItems: 1, maxItems: 5 }),
    attachments: Type.Array(AnnouncementAttachmentReadModelSchema, { maxItems: 3 }),
    createdByDisplayName: Type.String({ minLength: 1, maxLength: 120 }),
    publishedByDisplayName: Type.Union([
      Type.String({ minLength: 1, maxLength: 120 }),
      Type.Null(),
    ]),
    publishedAt: NullableDateTimeSchema,
    withdrawnByDisplayName: Type.Union([
      Type.String({ minLength: 1, maxLength: 120 }),
      Type.Null(),
    ]),
    withdrawnAt: NullableDateTimeSchema,
    withdrawalReason: Type.Union([Type.String({ minLength: 2, maxLength: 200 }), Type.Null()]),
    createdAt: DateTimeSchema,
    updatedAt: DateTimeSchema,
  },
  { additionalProperties: false },
);
export type AnnouncementReadModel = Static<typeof AnnouncementReadModelSchema>;

export const AnnouncementFeedItemSchema = Type.Object(
  {
    announcement: AnnouncementReadModelSchema,
    myRole: AnnouncementAudienceRoleSchema,
    myDeliveryState: AnnouncementDeliveryStateSchema,
    myReplies: Type.Array(AnnouncementReplyReadModelSchema, { maxItems: 100 }),
  },
  { additionalProperties: false },
);
export type AnnouncementFeedItem = Static<typeof AnnouncementFeedItemSchema>;

export const AnnouncementFeedSchema = Type.Object(
  { items: Type.Array(AnnouncementFeedItemSchema, { maxItems: 200 }), generatedAt: DateTimeSchema },
  { additionalProperties: false },
);
export type AnnouncementFeed = Static<typeof AnnouncementFeedSchema>;

export const AnnouncementManagementDetailSchema = Type.Object(
  {
    announcement: AnnouncementReadModelSchema,
    recipients: Type.Array(AnnouncementRecipientSummarySchema, { maxItems: 500 }),
    replies: Type.Array(AnnouncementReplyReadModelSchema, { maxItems: 1000 }),
  },
  { additionalProperties: false },
);
export type AnnouncementManagementDetail = Static<typeof AnnouncementManagementDetailSchema>;

export const AnnouncementMutationOperationSchema = Type.Union([
  Type.Literal("CREATE_DRAFT"),
  Type.Literal("UPDATE_DRAFT"),
  Type.Literal("PUBLISH"),
  Type.Literal("WITHDRAW"),
  Type.Literal("RETRY_DELIVERY"),
  Type.Literal("DELIVER"),
  Type.Literal("READ"),
  Type.Literal("ACKNOWLEDGE"),
  Type.Literal("REPLY"),
]);
export type AnnouncementMutationOperation = Static<typeof AnnouncementMutationOperationSchema>;

export const AnnouncementMutationResultSchema = Type.Object(
  {
    operation: AnnouncementMutationOperationSchema,
    announcement: AnnouncementReadModelSchema,
    deliveryState: Type.Union([AnnouncementDeliveryStateSchema, Type.Null()]),
    reply: Type.Union([AnnouncementReplyReadModelSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type AnnouncementMutationResult = Static<typeof AnnouncementMutationResultSchema>;

export const AnnouncementBusinessEventPayloadSchema = Type.Object(
  {
    announcement: AnnouncementReadModelSchema,
    recipients: Type.Array(AnnouncementRecipientSummarySchema, { maxItems: 500 }),
    actorAccountId: StableIdSchema,
    actorDisplayName: Type.String({ minLength: 1, maxLength: 120 }),
    actorRole: AnnouncementAudienceRoleSchema,
    operation: AnnouncementMutationOperationSchema,
    deliveryState: Type.Union([AnnouncementDeliveryStateSchema, Type.Null()]),
    reply: Type.Union([AnnouncementReplyReadModelSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type AnnouncementBusinessEventPayload = Static<
  typeof AnnouncementBusinessEventPayloadSchema
>;

function canonicalEpoch(value: string): number | null {
  const epoch = Date.parse(value);
  return Number.isFinite(epoch) && new Date(epoch).toISOString() === value ? epoch : null;
}

function isRealDate(value: string): boolean {
  const epoch = Date.parse(`${value}T00:00:00.000Z`);
  return (
    /^\d{4}-\d{2}-\d{2}$/u.test(value) &&
    Number.isFinite(epoch) &&
    new Date(epoch).toISOString() === `${value}T00:00:00.000Z`
  );
}

function deriveHex(value: string): string {
  let hash = FNV_1A_64_OFFSET;
  for (const character of value) {
    hash ^= BigInt(character.codePointAt(0) ?? 0);
    hash = BigInt.asUintN(64, hash * FNV_1A_64_PRIME);
  }
  return (hash & LOWER_48_BITS).toString(16).toUpperCase().padStart(12, "0");
}

export function deriveAnnouncementNumber(
  businessDate: string,
  announcementId: string,
): AnnouncementNumber {
  if (!isRealDate(businessDate) || !/^[A-Za-z0-9][A-Za-z0-9._:-]{11,127}$/u.test(announcementId)) {
    throw new TypeError("announcement identity is invalid");
  }
  return `GG-${businessDate.replaceAll("-", "")}-${deriveHex(`${businessDate}\0${announcementId}`)}`;
}

export function isAnnouncementNumber(value: unknown): value is AnnouncementNumber {
  if (!Value.Check(AnnouncementNumberSchema, value)) return false;
  const match = ANNOUNCEMENT_NUMBER_PATTERN.exec(value);
  return Boolean(match && isRealDate(`${match[1]}-${match[2]}-${match[3]}`));
}

function isCanonicalBase64(value: string, expectedBytes: number): boolean {
  if (!/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/u.test(value))
    return false;
  const padding = value.endsWith("==") ? 2 : value.endsWith("=") ? 1 : 0;
  return (value.length / 4) * 3 - padding === expectedBytes;
}

function hasExpectedAttachmentExtension(
  attachment: Pick<AnnouncementAttachmentInput, "fileName" | "contentType">,
): boolean {
  const lower = attachment.fileName.toLowerCase();
  switch (attachment.contentType) {
    case "application/pdf":
      return lower.endsWith(".pdf");
    case "image/jpeg":
      return lower.endsWith(".jpg") || lower.endsWith(".jpeg");
    case "image/png":
      return lower.endsWith(".png");
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      return lower.endsWith(".docx");
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      return lower.endsWith(".xlsx");
  }
}

function hasOnlyPrintableFileNameCharacters(value: string): boolean {
  return !/[\p{Cc}\p{Cf}]/u.test(value);
}

function hasSafeAttachmentFileName(
  attachment: Pick<AnnouncementAttachmentInput, "fileName" | "contentType">,
): boolean {
  return (
    attachment.fileName === attachment.fileName.trim() &&
    !attachment.fileName.includes("/") &&
    !attachment.fileName.includes("\\") &&
    attachment.fileName !== "." &&
    attachment.fileName !== ".." &&
    hasOnlyPrintableFileNameCharacters(attachment.fileName) &&
    hasExpectedAttachmentExtension(attachment)
  );
}

function validContent(
  value: CreateAnnouncementDraftRequest | UpdateAnnouncementDraftRequest,
): boolean {
  const startsAt = canonicalEpoch(value.startsAt);
  const expiresAt = canonicalEpoch(value.expiresAt);
  return (
    value.title === value.title.trim() &&
    value.body === value.body.trim() &&
    startsAt !== null &&
    expiresAt !== null &&
    expiresAt > startsAt &&
    new Set(value.audienceRoles).size === value.audienceRoles.length &&
    value.attachments.reduce((sum, item) => sum + item.sizeBytes, 0) <=
      MAX_TOTAL_ATTACHMENT_BYTES &&
    value.attachments.every(
      (item) =>
        hasSafeAttachmentFileName(item) && isCanonicalBase64(item.contentBase64, item.sizeBytes),
    )
  );
}

export function isCreateAnnouncementDraftRequest(
  value: unknown,
): value is CreateAnnouncementDraftRequest {
  return Value.Check(CreateAnnouncementDraftRequestSchema, value) && validContent(value);
}
export function isUpdateAnnouncementDraftRequest(
  value: unknown,
): value is UpdateAnnouncementDraftRequest {
  return Value.Check(UpdateAnnouncementDraftRequestSchema, value) && validContent(value);
}
export function isPublishAnnouncementRequest(value: unknown): value is PublishAnnouncementRequest {
  return Value.Check(PublishAnnouncementRequestSchema, value);
}
export function isWithdrawAnnouncementRequest(
  value: unknown,
): value is WithdrawAnnouncementRequest {
  return (
    Value.Check(WithdrawAnnouncementRequestSchema, value) && value.reason === value.reason.trim()
  );
}
export function isRetryAnnouncementDeliveryRequest(
  value: unknown,
): value is RetryAnnouncementDeliveryRequest {
  return Value.Check(RetryAnnouncementDeliveryRequestSchema, value);
}
export function isMarkAnnouncementDeliveryRequest(
  value: unknown,
): value is MarkAnnouncementDeliveryRequest {
  return Value.Check(MarkAnnouncementDeliveryRequestSchema, value);
}
export function isReplyAnnouncementRequest(value: unknown): value is ReplyAnnouncementRequest {
  return Value.Check(ReplyAnnouncementRequestSchema, value) && value.body === value.body.trim();
}

export function isAnnouncementReadModel(value: unknown): value is AnnouncementReadModel {
  if (!Value.Check(AnnouncementReadModelSchema, value)) return false;
  const model = value as AnnouncementReadModel;
  const createdAt = canonicalEpoch(model.createdAt);
  const updatedAt = canonicalEpoch(model.updatedAt);
  const startsAt = canonicalEpoch(model.startsAt);
  const expiresAt = canonicalEpoch(model.expiresAt);
  const publishedAt = model.publishedAt === null ? null : canonicalEpoch(model.publishedAt);
  const withdrawnAt = model.withdrawnAt === null ? null : canonicalEpoch(model.withdrawnAt);
  const numberMatch = ANNOUNCEMENT_NUMBER_PATTERN.exec(model.announcementNumber);
  const numberDate = numberMatch ? `${numberMatch[1]}-${numberMatch[2]}-${numberMatch[3]}` : null;
  let expectedAnnouncementNumber: AnnouncementNumber | null = null;
  if (numberDate !== null) {
    try {
      expectedAnnouncementNumber = deriveAnnouncementNumber(numberDate, model.announcementId);
    } catch {
      expectedAnnouncementNumber = null;
    }
  }
  if (
    !isAnnouncementNumber(model.announcementNumber) ||
    numberDate === null ||
    expectedAnnouncementNumber !== model.announcementNumber ||
    startsAt === null ||
    expiresAt === null ||
    createdAt === null ||
    updatedAt === null ||
    updatedAt < createdAt ||
    model.eventRevision < model.version ||
    expiresAt <= startsAt ||
    (model.publishedAt !== null && publishedAt === null) ||
    (model.withdrawnAt !== null && withdrawnAt === null) ||
    new Set(model.audienceRoles).size !== model.audienceRoles.length ||
    new Set(model.attachments.map((attachment) => attachment.attachmentId)).size !==
      model.attachments.length ||
    model.attachments.reduce((sum, attachment) => sum + attachment.sizeBytes, 0) >
      MAX_TOTAL_ATTACHMENT_BYTES ||
    model.attachments.some(
      (attachment) =>
        !hasSafeAttachmentFileName(attachment) ||
        attachment.downloadPath !==
          `/v1/announcements/${model.announcementId}/attachments/${attachment.attachmentId}`,
    )
  ) {
    return false;
  }
  if (model.lifecycle === "DRAFT") {
    return (
      model.publishedByDisplayName === null &&
      publishedAt === null &&
      model.withdrawnByDisplayName === null &&
      withdrawnAt === null &&
      model.withdrawalReason === null
    );
  }
  if (model.lifecycle === "WITHDRAWN") {
    return (
      model.publishedByDisplayName !== null &&
      publishedAt !== null &&
      model.withdrawnByDisplayName !== null &&
      withdrawnAt !== null &&
      withdrawnAt >= publishedAt &&
      model.withdrawalReason !== null
    );
  }
  return (
    model.publishedByDisplayName !== null &&
    publishedAt !== null &&
    model.withdrawnByDisplayName === null &&
    withdrawnAt === null &&
    model.withdrawalReason === null
  );
}
export function isAnnouncementFeed(value: unknown): value is AnnouncementFeed {
  return (
    Value.Check(AnnouncementFeedSchema, value) &&
    canonicalEpoch(value.generatedAt) !== null &&
    value.items.every((item) => isAnnouncementReadModel(item.announcement))
  );
}
export function isAnnouncementManagementDetail(
  value: unknown,
): value is AnnouncementManagementDetail {
  return (
    Value.Check(AnnouncementManagementDetailSchema, value) &&
    isAnnouncementReadModel(value.announcement) &&
    new Set(value.recipients.map((recipient) => recipient.accountId)).size ===
      value.recipients.length &&
    value.recipients.every((recipient) =>
      value.announcement.audienceRoles.includes(recipient.role),
    ) &&
    value.replies.every(
      (reply) =>
        canonicalEpoch(reply.repliedAt) !== null &&
        value.announcement.audienceRoles.includes(reply.authorRole),
    )
  );
}
export function isAnnouncementMutationResult(value: unknown): value is AnnouncementMutationResult {
  if (
    !Value.Check(AnnouncementMutationResultSchema, value) ||
    !isAnnouncementReadModel(value.announcement)
  ) {
    return false;
  }
  if (value.operation === "REPLY") {
    return value.reply !== null && value.deliveryState !== null;
  }
  if (["DELIVER", "READ", "ACKNOWLEDGE"].includes(value.operation)) {
    return value.reply === null && value.deliveryState !== null;
  }
  return value.reply === null && value.deliveryState === null;
}
export function isAnnouncementBusinessEventPayload(
  value: unknown,
): value is AnnouncementBusinessEventPayload {
  if (
    !Value.Check(AnnouncementBusinessEventPayloadSchema, value) ||
    !isAnnouncementReadModel(value.announcement) ||
    new Set(value.recipients.map((recipient) => recipient.accountId)).size !==
      value.recipients.length ||
    value.recipients.some((recipient) => !value.announcement.audienceRoles.includes(recipient.role))
  ) {
    return false;
  }
  const validRecipientTimeline = value.recipients.every((recipient) => {
    const deliveredAt =
      recipient.deliveredAt === null ? null : canonicalEpoch(recipient.deliveredAt);
    const readAt = recipient.readAt === null ? null : canonicalEpoch(recipient.readAt);
    const acknowledgedAt =
      recipient.acknowledgedAt === null ? null : canonicalEpoch(recipient.acknowledgedAt);
    if (
      (recipient.deliveredAt !== null && deliveredAt === null) ||
      (recipient.readAt !== null && readAt === null) ||
      (recipient.acknowledgedAt !== null && acknowledgedAt === null) ||
      (readAt !== null && (deliveredAt === null || readAt < deliveredAt)) ||
      (acknowledgedAt !== null && (readAt === null || acknowledgedAt < readAt))
    ) {
      return false;
    }
    if (recipient.state === "PENDING") {
      return deliveredAt === null && readAt === null && acknowledgedAt === null;
    }
    if (recipient.state === "DELIVERED") {
      return deliveredAt !== null && readAt === null && acknowledgedAt === null;
    }
    if (recipient.state === "READ") {
      return deliveredAt !== null && readAt !== null && acknowledgedAt === null;
    }
    return deliveredAt !== null && readAt !== null && acknowledgedAt !== null;
  });
  if (!validRecipientTimeline) return false;
  if (value.operation === "CREATE_DRAFT" || value.operation === "UPDATE_DRAFT") {
    return value.recipients.length === 0 && value.deliveryState === null && value.reply === null;
  }
  if (value.operation === "PUBLISH") {
    return value.recipients.length > 0 && value.deliveryState === null && value.reply === null;
  }
  if (value.operation === "WITHDRAW" || value.operation === "RETRY_DELIVERY") {
    return value.recipients.length > 0 && value.deliveryState === null && value.reply === null;
  }
  const actorRecipient = value.recipients.find(
    (recipient) => recipient.accountId === value.actorAccountId,
  );
  if (!actorRecipient) return false;
  if (value.operation === "REPLY") {
    return (
      value.reply !== null &&
      value.reply.authorDisplayName === value.actorDisplayName &&
      value.reply.authorRole === value.actorRole &&
      value.deliveryState === actorRecipient.state
    );
  }
  return (
    value.reply === null &&
    value.deliveryState === actorRecipient.state &&
    value.deliveryState ===
      (value.operation === "DELIVER"
        ? "DELIVERED"
        : value.operation === "READ"
          ? "READ"
          : "ACKNOWLEDGED")
  );
}
